import { getEpaFormBox } from "encamp-shared/src/waste/discrepancyFieldMeta";
import { type BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";

import { getFieldLabel, sortTerminalLast } from "./DiscrepancyReviewDialog.helpers";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/** chevron, checkbox, [Facility, Manifest], Waste line, e-Manifest, Vendor, Document, Status. */
export function getColCount(showManifestColumns: boolean): number {
  return showManifestColumns ? 9 : 7;
}

export interface WasteLineDescriptionFields {
  wasteLineUnifiedId?: string | null;
  wasteLineNumber?: string | null;
  wasteLineDotDescription?: string | null;
  field: string;
}

/**
 * A conflict row's subject line: which waste line the finding is about (or
 * "Manifest" for a manifest-level field), the line's shipping description,
 * and its EPA Form 8700-22 box — e.g. `Line 4 · UN1993 Waste flammable
 * liquid n.o.s. · Box 19`.
 */
export function describeWasteLine(d: WasteLineDescriptionFields): {
  primary: string;
  secondary: string | null;
} {
  const primary = d.wasteLineUnifiedId ? `Line ${d.wasteLineNumber ?? "item"}` : "Manifest";
  const parts: string[] = [];
  if (d.wasteLineDotDescription) parts.push(d.wasteLineDotDescription);
  const box = getEpaFormBox(d.field);
  if (box != null) parts.push(`Box ${box}`);
  return { primary, secondary: parts.length > 0 ? parts.join(" · ") : null };
}

/**
 * A field group's shared held-back reason: the reason every row in the group
 * agrees on, or null when any row lacks one or they disagree. Nothing writes
 * `heldBackReason` yet, so this returns null for every group in production
 * today — the rendering it drives is built ahead of that later workstream.
 */
export function getGroupHeldBackReason(
  items: readonly { heldBackReason?: string | null }[]
): string | null {
  let reason: string | null = null;
  for (const item of items) {
    if (item.heldBackReason == null) return null;
    if (reason == null) {
      reason = item.heldBackReason;
    } else if (item.heldBackReason !== reason) {
      return null;
    }
  }
  return reason;
}

export interface FieldGroup {
  field: string;
  label: string;
  items: Discrepancy[];
  heldBackReason: string | null;
}

export function buildFieldGroups(discrepancies: readonly Discrepancy[]): FieldGroup[] {
  const groups = new Map<string, Discrepancy[]>();
  for (const d of discrepancies) {
    const existing = groups.get(d.field);
    if (existing) {
      existing.push(d);
    } else {
      groups.set(d.field, [d]);
    }
  }
  return [...groups.entries()].map(([field, items]) => ({
    field,
    label: getFieldLabel(field),
    items: sortTerminalLast(items),
    heldBackReason: getGroupHeldBackReason(items),
  }));
}

/** The schema documents a null verdict as "not yet recomputed" — never fall back to comparing raw values here. */
export function verdictText(value: boolean | null | undefined): string {
  if (value == null) return "not yet recomputed";
  return value ? "yes" : "no";
}
